import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Stack,
  Avatar,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Collapse,
  ButtonBase,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  ContentCopy as CopyIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  EventNote as EventNoteIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Restaurant as RestaurantIcon,
  Celebration as ActivityIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PdfIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import api from '../config/api';
import { getTripPrimaryLocation, canEditTripItinerary, showPaidAiBadge } from '../utils/tripUtils';
import ActivityModal from '../components/ActivityModal';
import TripItinerary from '../components/TripItinerary';
import TripDocuments from '../components/TripDocuments';
import PartnerGroupChat from '../components/PartnerGroupChat';
import PartnerGroupAddOns from '../components/PartnerGroupAddOns';
import TripExpenses from '../components/TripExpenses';
import PartnerTripAIDrawer from '../components/PartnerTripAIDrawer';
import PartnerTripDetailsEditor from '../components/PartnerTripDetailsEditor';
import { PaidFeatureLabel } from '../components/DocumentProBadge';
import { DOCUMENT, documentSubTabsSx, documentTabIndicatorSx } from '../constants/documentTheme';
import {
  PartnerLoading,
  PartnerPageShell,
  PartnerTripHero,
  partnerOxbloodBtnSx,
  partnerOutlineBtnSx,
  partnerLinkSx,
} from '../components/PartnerListChrome';
import { getTravelerDisplayName, getTravelerInitials } from '../utils/partnerListUtils';
import { partnerTripsReturnPath } from '../utils/partnerWorkspace';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import PartnerGroupBilling from '../components/groupBilling/PartnerGroupBilling';
import { BillingStatusChip } from '../components/groupBilling/BillingParts';
import { formatMoney } from '../utils/partnerFinance';
import PartnerTripFinancePanel, {
  StatusBadge,
  financeFormToPayload,
  financeToForm,
  resolvePanelPaymentStatus,
  rollupMemberFinance,
} from '../components/PartnerTripFinancePanel';
import PdfExportProgressDialog from '../components/PdfExportProgressDialog';
import useTripPdfExport from '../hooks/useTripPdfExport';

const getCategoryIcon = (category) => {
  switch (category) {
    case 'hotel': return <HotelIcon />;
    case 'restaurant': return <RestaurantIcon />;
    case 'transport': return <FlightIcon />;
    case 'activity': return <ActivityIcon />;
    case 'event': return <EventNoteIcon />;
    default: return <EventNoteIcon />;
  }
};

const PartnerMasterTripDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Data
  const [masterTrip, setMasterTrip] = useState(null);
  const [childTrips, setChildTrips] = useState([]);
  const [sharedItinerary, setSharedItinerary] = useState([]);
  const travelerTargets = useMemo(
    () => childTrips.map((child) => ({ tripId: child.id, label: getTravelerDisplayName(child) })),
    [childTrips]
  );
  const travelerItemCount = childTrips.reduce((sum, child) => sum + (Number(child.personal_item_count) || 0), 0);
  
  // Dialogs
  const [addTravelerOpen, setAddTravelerOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [viewTravelerOpen, setViewTravelerOpen] = useState(null);
  const [addPersonalItemOpen, setAddPersonalItemOpen] = useState(null);
  const [confirmDeleteTraveler, setConfirmDeleteTraveler] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [pdfDownloadingId, setPdfDownloadingId] = useState(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);
  const {
    downloadTripPdf,
    pdfProgressOpen,
    pdfProgress,
    pdfProgressPhase,
  } = useTripPdfExport();
  const itineraryRef = useRef();
  
  // Form states
  const [travelerForm, setTravelerForm] = useState({ email: '', firstName: '', lastName: '', notes: '' });
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Traveler detail data
  const [travelerDetail, setTravelerDetail] = useState(null);
  const [financeForms, setFinanceForms] = useState({});
  const [paidFromPaymentsByChild, setPaidFromPaymentsByChild] = useState({});
  const [financeSavingId, setFinanceSavingId] = useState(null);
  const [paymentSavingId, setPaymentSavingId] = useState(null);
  const [groupFinanceForm, setGroupFinanceForm] = useState(financeToForm(null));
  const [groupFinanceSaving, setGroupFinanceSaving] = useState(false);
  // Traveler cards start collapsed; only ids listed here are expanded
  const [expandedTravelerIds, setExpandedTravelerIds] = useState(() => new Set());
  const { entitlements } = usePartnerEntitlements();

  const toggleTravelerExpanded = (childId) => {
    setExpandedTravelerIds((prev) => {
      const next = new Set(prev);
      if (next.has(childId)) next.delete(childId);
      else next.add(childId);
      return next;
    });
  };

  // Stable reference for personal item defaults (prevents ActivityModal re-init on every render)
  const personalItemDefaults = useMemo(() => ({ category: 'transport' }), []);
  const tripForFeatures = useMemo(
    () => (masterTrip ? { ...masterTrip, managed_by_partner: true } : null),
    [masterTrip]
  );
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const ledgerActive = Boolean(billing?.active);
  const billingTravelerById = useMemo(
    () => new Map((billing?.travelers || []).map((tr) => [tr.id, tr])),
    [billing]
  );
  const groupRollup = useMemo(() => {
    if (ledgerActive) {
      return { total: billing.summary.sold, paid: billing.summary.collected, outstanding: billing.summary.outstanding };
    }
    return rollupMemberFinance(Object.values(financeForms));
  }, [financeForms, ledgerActive, billing]);

  const fetchBilling = async () => {
    try {
      setBillingLoading(true);
      setBillingError('');
      const response = await api.get(`/partners/trips/${id}/billing`, { usePartnerAuth: true });
      setBilling(response.data?.billing || null);
    } catch (err) {
      setBillingError(err.response?.data?.error || t('groupBilling.errors.load'));
    } finally {
      setBillingLoading(false);
    }
  };

  const handleBillingChange = (next) => {
    setBilling(next);
    if (next?.active) {
      setGroupFinanceForm((prev) => ({ ...prev, commissionExpected: String(next.summary.commissionExpected ?? '') }));
    }
  };

  useEffect(() => {
    fetchMasterTrip();
    fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMasterTrip = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('partnerToken');
      
      if (!token) {
        navigate('/partner/login');
        return;
      }

      const response = await api.get(`/partners/master-trips/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMasterTrip(response.data.masterTrip);
      setChildTrips(response.data.childTrips);
      setSharedItinerary(response.data.sharedItinerary);
      const nextForms = {};
      const nextPaidFrom = {};
      (response.data.childTrips || []).forEach((child) => {
        nextForms[child.id] = financeToForm(child.finance);
        nextPaidFrom[child.id] = Boolean(child.finance?.paidFromPayments);
      });
      setFinanceForms(nextForms);
      setPaidFromPaymentsByChild(nextPaidFrom);
      setGroupFinanceForm(financeToForm(response.data.groupFinance));
    } catch (err) {
      console.error('Error fetching master trip:', err);
      if (err.response?.status === 401) {
        navigate('/partner/login');
      } else {
        setError(t('partnerManage.loadGroupFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddTraveler = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('partnerToken');
      
      await api.post(`/partners/master-trips/${id}/travelers`, travelerForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(t('partnerManage.travelerAdded'));
      setAddTravelerOpen(false);
      setTravelerForm({ email: '', firstName: '', lastName: '', notes: '' });
      fetchMasterTrip();
      fetchBilling();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add traveler');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveTraveler = async (childTripId) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('partnerToken');
      
      await api.delete(`/partners/master-trips/${id}/travelers/${childTripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(t('partnerManage.travelerRemoved'));
      setConfirmDeleteTraveler(null);
      fetchMasterTrip();
      fetchBilling();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove traveler');
    } finally {
      setSubmitting(false);
    }
  };

  const applyChildFinance = (childTripId, next) => {
    setFinanceForms((prev) => ({ ...prev, [childTripId]: financeToForm(next) }));
    setPaidFromPaymentsByChild((prev) => ({ ...prev, [childTripId]: Boolean(next?.paidFromPayments) }));
    setChildTrips((prev) =>
      prev.map((child) => (child.id === childTripId ? { ...child, finance: next } : child))
    );
  };

  const saveTravelerFinance = async (childTripId) => {
    const form = financeForms[childTripId];
    if (!form) return;
    try {
      setFinanceSavingId(childTripId);
      const response = await api.patch(
        `/partners/trips/${childTripId}/finance`,
        financeFormToPayload(
          { ...form, paidFromPayments: paidFromPaymentsByChild[childTripId] },
          'member'
        ),
        { usePartnerAuth: true }
      );
      applyChildFinance(childTripId, response.data?.finance || null);
      setSuccess(t('partnerFinance.saved'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerFinance.saveFailed'));
    } finally {
      setFinanceSavingId(null);
    }
  };

  const addTravelerPayment = async (childTripId, payment) => {
    try {
      setPaymentSavingId(childTripId);
      const response = await api.post(
        `/partners/trips/${childTripId}/finance/payments`,
        payment,
        { usePartnerAuth: true }
      );
      applyChildFinance(childTripId, response.data?.finance || null);
      setSuccess(t('partnerFinance.saved'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerFinance.paymentFailed'));
    } finally {
      setPaymentSavingId(null);
    }
  };

  const deleteTravelerPayment = async (childTripId, paymentId) => {
    try {
      setPaymentSavingId(childTripId);
      const response = await api.delete(
        `/partners/trips/${childTripId}/finance/payments/${paymentId}`,
        { usePartnerAuth: true }
      );
      applyChildFinance(childTripId, response.data?.finance || null);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerFinance.paymentFailed'));
    } finally {
      setPaymentSavingId(null);
    }
  };

  const saveGroupFinance = async () => {
    if (!masterTrip?.id) return;
    try {
      setGroupFinanceSaving(true);
      const response = await api.patch(
        `/partners/trips/${masterTrip.id}/finance`,
        financeFormToPayload(groupFinanceForm, 'commission'),
        { usePartnerAuth: true }
      );
      setGroupFinanceForm(financeToForm(response.data?.finance));
      setSuccess(t('partnerFinance.saved'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerFinance.saveFailed'));
    } finally {
      setGroupFinanceSaving(false);
    }
  };

  const handleAddSharedItem = async (formData) => {
    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('partnerToken');
      
      await api.post(`/partners/master-trips/${id}/shared-itinerary`, {
        name: formData.name,
        location: formData.location,
        googleMapLink: formData.google_map_link,
        category: formData.category,
        transportationType: formData.transportation_type,
        transportationData: formData.transportation_data,
        startDatetime: formData.start_datetime,
        endDatetime: formData.end_datetime,
        description: formData.description,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        costCurrency: formData.cost_currency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(t('partnerManage.sharedItemAdded'));
      setAddItemOpen(false);
      fetchMasterTrip();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add itinerary item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPersonalItem = async (formData) => {
    const childTripId = addPersonalItemOpen;
    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('partnerToken');
      
      await api.post(`/partners/master-trips/${id}/travelers/${childTripId}/itinerary`, {
        name: formData.name,
        location: formData.location,
        googleMapLink: formData.google_map_link,
        category: formData.category,
        transportationType: formData.transportation_type,
        transportationData: formData.transportation_data,
        startDatetime: formData.start_datetime,
        endDatetime: formData.end_datetime,
        description: formData.description,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        costCurrency: formData.cost_currency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(t('partnerManage.personalItemAdded'));
      setAddPersonalItemOpen(null);
      // Refresh master trip data to update personal item counts
      fetchMasterTrip();
      // Refresh traveler detail if open
      if (viewTravelerOpen) {
        handleViewTraveler(childTripId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add personal item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBroadcast = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('partnerToken');
      
      await api.post(`/partners/master-trips/${id}/broadcast`, {
        content: broadcastMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(t('partnerManage.messageSent'));
      setBroadcastOpen(false);
      setBroadcastMessage('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTraveler = async (childTripId) => {
    try {
      const token = localStorage.getItem('partnerToken');
      const response = await api.get(`/partners/master-trips/${id}/travelers/${childTripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTravelerDetail(response.data);
      setViewTravelerOpen(childTripId);
    } catch (err) {
      setError('Failed to load traveler details');
    }
  };

  const handleDownloadTravelerPdf = async (child) => {
    if (!child?.id || pdfDownloadingId || pdfProgressOpen) return;
    try {
      setPdfDownloadingId(child.id);
      setError('');
      const traveler = getTravelerDisplayName(child).replace(/[^a-zA-Z0-9]+/g, '_');
      const title = String(masterTrip?.title || 'trip').replace(/[^a-zA-Z0-9]+/g, '_');
      await downloadTripPdf({
        tripId: masterTrip.id,
        tripTitle: `${title}_${traveler}`,
        usePartnerAuth: true,
        params: { travelerTripId: child.id },
      });
      setSuccess(t('partnerManage.travelerPdfDownloaded', { name: getTravelerDisplayName(child) }));
    } catch (err) {
      let message = t('partnerManage.pdfDownloadFailed');
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.error) message = parsed.error;
        } catch {
          // keep default
        }
      } else if (data?.error || err.message) {
        message = data?.error || err.message;
      }
      setError(message);
    } finally {
      setPdfDownloadingId(null);
    }
  };

  const copyTripCode = () => {
    if (masterTrip?.trip_code) {
      navigator.clipboard.writeText(masterTrip.trip_code);
      setSuccess(t('partnerManage.codeCopied'));
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return '';
    try {
      return format(new Date(dt), 'MMM d, yyyy h:mm a');
    } catch {
      return dt;
    }
  };

  if (loading) {
    return (
      <PartnerPageShell>
        <PartnerLoading />
      </PartnerPageShell>
    );
  }

  if (!masterTrip) {
    return (
      <PartnerPageShell error={t('partnerManage.groupTripNotFound')}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(partnerTripsReturnPath())} sx={partnerLinkSx}>
          {t('partnerManage.backToTrips')}
        </Button>
      </PartnerPageShell>
    );
  }

  const location = getTripPrimaryLocation(masterTrip);

  return (
    <PartnerPageShell error={error} onClearError={() => setError('')} success={success} onClearSuccess={() => setSuccess('')}>
      <Box sx={{ pt: 4.5 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(partnerTripsReturnPath())} sx={partnerLinkSx}>
          {t('partnerManage.backToGroupTrips')}
        </Button>
      </Box>

      <PartnerTripHero
        title={masterTrip.title}
        description={masterTrip.description}
        coverSrc={masterTrip.image_url && String(masterTrip.image_url).trim() ? masterTrip.image_url : null}
        dates={`${masterTrip.start_date ? format(new Date(masterTrip.start_date), 'EEE, MMM d') : t('partnerLists.dash')} – ${masterTrip.end_date ? format(new Date(masterTrip.end_date), 'EEE, MMM d, yyyy') : t('partnerLists.dash')}`}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 2.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 4 } }}>
            {location && location !== 'No destination set' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon sx={{ fontSize: 16, color: DOCUMENT.accent }} />
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ivory }}>{location}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 16, color: DOCUMENT.accent }} />
              <Typography sx={{ fontSize: 14, color: DOCUMENT.ivory }}>
                {t('partnerManage.travelersMeta', { count: childTrips.length })}
              </Typography>
            </Box>
          </Box>
          {masterTrip.trip_code && (
            <Tooltip title={t('partnerManage.copyCode')}>
              <Chip
                icon={<CopyIcon sx={{ color: `${DOCUMENT.ivory} !important`, fontSize: 16 }} />}
                label={t('partnerManage.codeLabel', { code: masterTrip.trip_code })}
                onClick={copyTripCode}
                sx={{
                  fontFamily: DOCUMENT.sans,
                  fontWeight: 600,
                  backgroundColor: 'rgba(246,239,228,0.12)',
                  color: DOCUMENT.ivory,
                  border: `1px solid ${DOCUMENT.navRule}`,
                  '&:hover': { backgroundColor: 'rgba(246,239,228,0.2)' },
                }}
              />
            </Tooltip>
          )}
        </Box>
      </PartnerTripHero>

      <Stack direction="row" spacing={1.5} sx={{ my: 3 }} flexWrap="wrap" useFlexGap>
        <Button startIcon={<EditIcon />} onClick={() => setEditDetailsOpen(true)} sx={partnerOutlineBtnSx}>
          {t('partnerManage.editDetails')}
        </Button>
        {canEditTripItinerary(tripForFeatures) && (
        <Button onClick={() => setAiOpen(true)} sx={partnerOutlineBtnSx} data-testid="partner-trip-ai">
          <PaidFeatureLabel show={showPaidAiBadge(tripForFeatures, { entitlements })}>
            {t('partnerManage.suggestWithAi')}
          </PaidFeatureLabel>
        </Button>
        )}
        <Button startIcon={<PersonAddIcon />} onClick={() => setAddTravelerOpen(true)} sx={partnerOxbloodBtnSx}>
          {t('partnerManage.addTraveler')}
        </Button>
        {canEditTripItinerary(tripForFeatures) && (
        <Button startIcon={<AddIcon />} onClick={() => setAddItemOpen(true)} sx={partnerOutlineBtnSx}>
          {t('partnerManage.addSharedActivity')}
        </Button>
        )}
        <Button startIcon={<SendIcon />} onClick={() => setBroadcastOpen(true)} sx={partnerOutlineBtnSx}>
          {t('partnerManage.messageAll')}
        </Button>
        <Button
          startIcon={pdfDownloadingId
            ? <CircularProgress size={16} color="inherit" />
            : <PdfIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={(event) => setPdfMenuAnchor(event.currentTarget)}
          disabled={Boolean(pdfDownloadingId) || childTrips.length === 0}
          sx={partnerOutlineBtnSx}
          data-testid="partner-group-download-pdf"
        >
          {pdfDownloadingId
            ? t('partnerManage.pdfDownloading')
            : t('partnerManage.downloadPdf')}
        </Button>
        <Menu
          anchorEl={pdfMenuAnchor}
          open={Boolean(pdfMenuAnchor)}
          onClose={() => setPdfMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          data-testid="partner-group-pdf-menu"
        >
          <MenuItem disabled sx={{ opacity: 1, fontSize: 12, color: DOCUMENT.ink3 }}>
            {t('partnerManage.downloadPdfPickTraveler')}
          </MenuItem>
          {childTrips.map((child) => (
            <MenuItem
              key={child.id}
              data-testid={`partner-group-pdf-traveler-${child.id}`}
              disabled={Boolean(pdfDownloadingId)}
              onClick={() => {
                setPdfMenuAnchor(null);
                handleDownloadTravelerPdf(child);
              }}
            >
              <ListItemText primary={getTravelerDisplayName(child)} />
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      <Box sx={{ bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
        <Box sx={{ px: { xs: 2, md: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => {
              setActiveTab(v);
              if (v === 2) fetchBilling();
            }}
            TabIndicatorProps={{ sx: documentTabIndicatorSx }}
            sx={documentSubTabsSx}
          >
            <Tab label={`${t('tripTabs.itinerary')} (${sharedItinerary.length + travelerItemCount})`} />
            <Tab label={`${t('partnerManage.travelers')} (${childTrips.length})`} />
            <Tab label={t('groupBilling.tab')} data-testid="partner-group-billing-tab" />
            <Tab label={t('partnerManage.documents')} />
            <Tab label={t('partnerManage.chat')} data-testid="partner-group-chat-tab" />
            <Tab label={t('partnerAddons.title')} data-testid="partner-group-addons-tab" />
            <Tab label={t('tripTabs.expenses')} data-testid="partner-group-expenses-tab" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {activeTab === 0 && (
            <TripItinerary
              ref={itineraryRef}
              tripId={masterTrip.id}
              isCreator={true}
              isPartner={true}
              trip={tripForFeatures}
              partnerEntitlements={entitlements}
              onSuggestWithAi={canEditTripItinerary(tripForFeatures)
                ? () => setAiOpen(true)
                : undefined}
              onItineraryChange={fetchMasterTrip}
            />
          )}

          {activeTab === 1 && (
            <Box>
            {childTrips.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, mb: 1 }}>
                  {t('partnerManage.noTravelers')}
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, mb: 2.5, maxWidth: 420, mx: 'auto' }}>
                  {t('partnerManage.noTravelersBody')}
                </Typography>
                <Button startIcon={<PersonAddIcon />} onClick={() => setAddTravelerOpen(true)} sx={partnerOxbloodBtnSx}>
                  {t('partnerManage.addFirstTraveler')}
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {childTrips.map((child) => {
                  const childFinanceForm = financeForms[child.id] || financeToForm(child.finance);
                  const childPaidFromPayments = paidFromPaymentsByChild[child.id]
                    ?? Boolean(child.finance?.paidFromPayments);
                  const { status: childPaymentStatus } = resolvePanelPaymentStatus({
                    form: childFinanceForm,
                    payments: child.finance?.payments || [],
                    paidFromPayments: childPaidFromPayments,
                  });
                  const expanded = expandedTravelerIds.has(child.id);
                  const detailsId = `partner-traveler-details-${child.id}`;
                  const billed = ledgerActive ? billingTravelerById.get(child.id) : null;

                  return (
                  <Grid item xs={12} key={child.id}>
                    <Card
                      data-testid="partner-traveler-card"
                      sx={{
                        bgcolor: DOCUMENT.surface,
                        border: `1px solid ${DOCUMENT.ruleHard}`,
                        borderRadius: 0,
                        boxShadow: 'none',
                      }}
                    >
                      <ButtonBase
                        onClick={() => toggleTravelerExpanded(child.id)}
                        aria-expanded={expanded}
                        aria-controls={detailsId}
                        aria-label={`${getTravelerDisplayName(child)}: ${expanded ? t('partnerManage.collapseTraveler') : t('partnerManage.expandTraveler')}`}
                        data-testid={`partner-traveler-toggle-${child.id}`}
                        sx={{
                          width: '100%',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          px: 3,
                          py: 2,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                          <Avatar sx={{ bgcolor: DOCUMENT.oxblood, width: 40, height: 40 }}>
                            {getTravelerInitials(child)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{ fontFamily: DOCUMENT.display, fontSize: 20 }}>
                              {getTravelerDisplayName(child)}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>
                              {child.email}
                            </Typography>
                          </Box>
                          {billed ? (
                            <BillingStatusChip
                              status={billed.status}
                              testId={`partner-traveler-status-${child.id}`}
                            />
                          ) : (
                            <StatusBadge
                              status={childPaymentStatus}
                              testId={`partner-traveler-status-${child.id}`}
                            />
                          )}
                          <ExpandMoreIcon
                            sx={{
                              color: DOCUMENT.ink3,
                              transition: 'transform 150ms',
                              transform: expanded ? 'rotate(180deg)' : 'none',
                            }}
                          />
                        </Stack>
                      </ButtonBase>

                      <Collapse in={expanded} timeout="auto" id={detailsId}>
                      <CardContent sx={{ px: 3, pt: 1, pb: 3 }}>
                        <Chip
                          icon={<EventNoteIcon sx={{ fontSize: 16 }} />}
                          label={t('partnerManage.personalItems', { count: child.personal_item_count || 0 })}
                          size="small"
                          sx={{ mb: 2, borderColor: DOCUMENT.ruleHard, color: DOCUMENT.ink2 }}
                          variant="outlined"
                        />

                        {billed ? (
                          <Box
                            data-testid={`partner-traveler-billing-${child.id}`}
                            sx={{ border: `1px solid ${DOCUMENT.rule}`, p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
                          >
                            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>
                              {t('groupBilling.travelerCard.share', {
                                amount: formatMoney(billed.share, billing.currency),
                                payer: billed.accountName,
                              })}
                            </Typography>
                            <Button size="small" sx={partnerLinkSx} onClick={() => setActiveTab(2)}>
                              {t('groupBilling.travelerCard.open')}
                            </Button>
                          </Box>
                        ) : (
                        <PartnerTripFinancePanel
                          variant="member"
                          form={childFinanceForm}
                          onChange={(patch) =>
                            setFinanceForms((prev) => ({
                              ...prev,
                              [child.id]: { ...(prev[child.id] || financeToForm(child.finance)), ...patch },
                            }))
                          }
                          onSave={() => saveTravelerFinance(child.id)}
                          saving={financeSavingId === child.id}
                          title={t('partnerFinance.memberPayments')}
                          showStatusChip
                          dense
                          testId={`partner-traveler-finance-${child.id}`}
                          payments={child.finance?.payments || []}
                          paidFromPayments={childPaidFromPayments}
                          onAddPayment={(payment) => addTravelerPayment(child.id, payment)}
                          onDeletePayment={(paymentId) => deleteTravelerPayment(child.id, paymentId)}
                          paymentSaving={paymentSavingId === child.id}
                        />
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                          <Button size="small" startIcon={<ViewIcon />}
                            onClick={() => handleViewTraveler(child.id)}
                            sx={{ ...partnerOutlineBtnSx, flex: 1, py: 1, minWidth: 120 }}>
                            {t('partnerManage.viewDetails')}
                          </Button>
                          <Button size="small" startIcon={<AddIcon />}
                            onClick={() => setAddPersonalItemOpen(child.id)}
                            sx={{ ...partnerOutlineBtnSx, flex: 1, py: 1, minWidth: 120 }}>
                            {t('partnerManage.addFlightHotel')}
                          </Button>
                          <Tooltip title={t('partnerManage.removeTraveler')}>
                            <IconButton size="small" onClick={() => setConfirmDeleteTraveler(child.id)} sx={{ color: DOCUMENT.oxblood }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                      </Collapse>
                    </Card>
                  </Grid>
                  );
                })}
              </Grid>
            )}
            </Box>
          )}

          {activeTab === 2 && (
            <PartnerGroupBilling
              masterTripId={masterTrip.id}
              billing={billing}
              loading={billingLoading}
              error={billingError}
              onBillingChange={handleBillingChange}
              onReload={fetchBilling}
              agencyPanel={(
                <PartnerTripFinancePanel
                  variant="group"
                  form={groupFinanceForm}
                  rollup={groupRollup}
                  onChange={(patch) => setGroupFinanceForm((p) => ({ ...p, ...patch }))}
                  onSave={saveGroupFinance}
                  saving={groupFinanceSaving}
                  title={t('partnerFinance.groupPayments')}
                  testId="partner-group-finance"
                />
              )}
            />
          )}

          {activeTab === 3 && (
            <TripDocuments
              tripId={masterTrip.id}
              isCreator={true}
              isPartner={true}
              isActive={activeTab === 3}
              audiences={travelerTargets}
            />
          )}

          {activeTab === 4 && (
            <PartnerGroupChat groupTripId={masterTrip.id} isActive={activeTab === 4} />
          )}

          {activeTab === 5 && (
            <PartnerGroupAddOns
              groupTripId={masterTrip.id}
              travelers={travelerTargets}
              isActive={activeTab === 5}
            />
          )}

          {activeTab === 6 && (
            <TripExpenses
              tripId={masterTrip.id}
              trip={tripForFeatures}
              isPartner
              assignableMembers={childTrips
                .filter((child) => child.user_id)
                .map((child) => ({
                  id: Number(child.user_id),
                  username: child.username,
                  email: child.email,
                  first_name: child.first_name,
                  last_name: child.last_name,
                }))}
            />
          )}
        </Box>
      </Box>

      {/* ============= DIALOGS ============= */}

      {/* Add Traveler Dialog */}
      <Dialog open={addTravelerOpen} onClose={() => setAddTravelerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>{t('partnerManage.addTraveler')}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField label={t('partnerManage.email')} type="email" fullWidth required
              value={travelerForm.email}
              onChange={(e) => setTravelerForm(prev => ({ ...prev, email: e.target.value }))}
              helperText={t('partnerManage.inviteHelper')}
            />
            <Stack direction="row" spacing={2}>
              <TextField label={t('partnerManage.firstName')} fullWidth
                value={travelerForm.firstName}
                onChange={(e) => setTravelerForm(prev => ({ ...prev, firstName: e.target.value }))} />
              <TextField label={t('partnerManage.lastName')} fullWidth
                value={travelerForm.lastName}
                onChange={(e) => setTravelerForm(prev => ({ ...prev, lastName: e.target.value }))} />
            </Stack>
            <TextField label={t('partnerManage.notes')} multiline rows={2} fullWidth
              value={travelerForm.notes}
              onChange={(e) => setTravelerForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={t('partnerManage.notesPlaceholder')} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setAddTravelerOpen(false)} sx={partnerLinkSx}>{t('partnerLists.cancel')}</Button>
          <Button onClick={handleAddTraveler}
            disabled={submitting || !travelerForm.email}
            sx={partnerOxbloodBtnSx}>
            {submitting ? <CircularProgress size={24} sx={{ color: DOCUMENT.ivory }} /> : t('partnerManage.addTraveler')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Shared Activity Modal */}
      <ActivityModal
        open={addItemOpen}
        onClose={() => { setAddItemOpen(false); setError(''); }}
        onSubmit={handleAddSharedItem}
        mode="create"
        submitting={submitting}
        submitError={addItemOpen ? error : null}
        onClearSubmitError={() => setError('')}
        showVisibility={false}
        trip={masterTrip}
      />

      {/* Add Personal Item Modal */}
      <ActivityModal
        open={addPersonalItemOpen !== null}
        onClose={() => { setAddPersonalItemOpen(null); setError(''); }}
        onSubmit={handleAddPersonalItem}
        mode="create"
        submitting={submitting}
        submitError={addPersonalItemOpen !== null ? error : null}
        onClearSubmitError={() => setError('')}
        showVisibility={false}
        trip={masterTrip}
        initialData={personalItemDefaults}
      />

      {/* Broadcast Message Dialog */}
      <Dialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
          {t('partnerManage.messageAllTitle')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, mb: 2 }}>
            {t('partnerManage.messageAllBody', { count: childTrips.length })}
          </Typography>
          <TextField
            label={t('partnerManage.message')}
            multiline
            rows={4}
            fullWidth
            required
            value={broadcastMessage}
            onChange={(e) => setBroadcastMessage(e.target.value)}
            placeholder={t('partnerManage.messagePlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${DOCUMENT.rule}` }}>
          <Button onClick={() => setBroadcastOpen(false)} sx={partnerLinkSx}>{t('partnerLists.cancel')}</Button>
          <Button startIcon={<SendIcon />}
            onClick={handleBroadcast}
            disabled={submitting || !broadcastMessage.trim()}
            sx={partnerOxbloodBtnSx}>
            {submitting ? <CircularProgress size={24} sx={{ color: DOCUMENT.ivory }} /> : t('partnerManage.sendToAll')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Traveler Detail Dialog */}
      <Dialog open={viewTravelerOpen !== null} onClose={() => { setViewTravelerOpen(null); setTravelerDetail(null); }} 
        maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
          <Stack direction="row" spacing={2} alignItems="center">
            {travelerDetail?.childTrip && (
              <>
                <Avatar sx={{ bgcolor: DOCUMENT.oxblood }}>
                  {getTravelerInitials(travelerDetail.childTrip)}
                </Avatar>
                <Box>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22 }}>
                    {getTravelerDisplayName(travelerDetail.childTrip)}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>
                    {travelerDetail.childTrip.email}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {!travelerDetail ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
            </Box>
          ) : (
            <>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 2 }}>
                {t('partnerManage.completeItinerary')}
              </Typography>

              {travelerDetail.mergedItinerary?.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: DOCUMENT.canvas, boxShadow: 'none' }}>
                  <Typography sx={{ color: DOCUMENT.ink3 }}>{t('partnerManage.noItems')}</Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  {travelerDetail.mergedItinerary?.map((item) => (
                    <Card key={`${item.item_source}-${item.id}`}
                      sx={{
                        border: `1px solid ${DOCUMENT.ruleHard}`,
                        borderRadius: 0,
                        bgcolor: DOCUMENT.surface,
                        boxShadow: 'none',
                      }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Avatar sx={{
                            bgcolor: DOCUMENT.oxblood,
                            width: 32, height: 32,
                            '& svg': { fontSize: 18 }
                          }}>
                            {getCategoryIcon(item.category)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle2" fontWeight={600} noWrap>
                                {item.name}
                              </Typography>
                              <Chip
                                label={item.item_source === 'shared' ? t('partnerManage.group') : t('partnerManage.personal')}
                                size="small"
                                sx={{
                                  height: 20, fontSize: 11,
                                  backgroundColor: item.item_source === 'shared' ? DOCUMENT.oxblood : DOCUMENT.ink2,
                                  color: DOCUMENT.ivory
                                }}
                              />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(item.start_datetime)}
                              {item.location && ` · ${item.location}`}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button startIcon={<AddIcon />}
                  onClick={() => setAddPersonalItemOpen(viewTravelerOpen)}
                  sx={partnerOutlineBtnSx}>
                  {t('partnerManage.addPersonal')}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${DOCUMENT.rule}` }}>
          <Button onClick={() => { setViewTravelerOpen(null); setTravelerDetail(null); }} sx={partnerLinkSx}>
            {t('partnerManage.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteTraveler !== null} onClose={() => setConfirmDeleteTraveler(null)}>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>{t('partnerManage.removeTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('partnerManage.removeBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteTraveler(null)} sx={partnerLinkSx}>{t('partnerLists.cancel')}</Button>
          <Button onClick={() => handleRemoveTraveler(confirmDeleteTraveler)}
            disabled={submitting}
            sx={partnerOxbloodBtnSx}>
            {submitting ? <CircularProgress size={24} sx={{ color: DOCUMENT.ivory }} /> : t('partnerManage.remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <PartnerTripDetailsEditor
        open={editDetailsOpen}
        onClose={() => setEditDetailsOpen(false)}
        trip={masterTrip}
        mode="group"
        onSaved={fetchMasterTrip}
      />

      <PartnerTripAIDrawer
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        trip={tripForFeatures}
        onAdded={async (created) => {
          setActiveTab(0);
          await fetchMasterTrip();
          await itineraryRef.current?.refreshItinerary?.();
          itineraryRef.current?.focusDrafts?.(created);
        }}
      />

      <PdfExportProgressDialog
        open={pdfProgressOpen}
        progress={pdfProgress}
        phase={pdfProgressPhase}
      />
    </PartnerPageShell>
  );
};

export default PartnerMasterTripDetail;
